/**
 * Pul miqdori va foiz formatlari.
 * API dan kelgan raqamlarda ham ishlatiladi.
 * Birliklar va valyuta yozuvi i18n orqali tilga mos.
 */

import i18n from "@/app/i18n";

/** toLocaleString uchun brauzer locale */
function getNumberLocale(): string {
  const lng = i18n.language?.split("-")[0] ?? "uz";
  const full = i18n.language ?? "uz_latn";
  if (full === "uz_latn" || full === "uz_cyrl") return "uz-UZ";
  if (lng === "ru") return "ru-RU";
  if (lng === "en") return "en-US";
  return "uz-UZ";
}

const UNIT_KEYS = [
  { limit: 1e12, key: "formatUnitTrlrd" },
  { limit: 1e9, key: "formatUnitMlrd" },
  { limit: 1e6, key: "formatUnitMln" },
  { limit: 1e3, key: "formatUnitMing" },
] as const;

/**
 * Sonni o'qilishi oson qisqa formatda (1.5 ming, 2.3 mln, 1.1 mlrd).
 * So'm qo'shilmaydi — faqat raqam + birlik (tilda tarjima).
 */
function formatShortUnit(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return "0";
  const locale = getNumberLocale();
  const a = Math.abs(amount);
  for (const { limit, key } of UNIT_KEYS) {
    if (a >= limit) {
      const value = amount / limit;
      const str =
        value >= 10
          ? String(Math.round(value))
          : value.toFixed(1).replace(/\.0$/, "");
      return `${str} ${i18n.t(key)}`;
    }
  }
  return amount.toLocaleString(locale);
}

/**
 * Pul miqdorini tilga qulay formatda: ming, mln, mlrd, trlrd yoki to'liq raqam.
 * Masalan: "10 ming so'm", "1.5 mln so'm" (tilga qarab).
 */
export function formatMoney(amount: number): string {
  const currency = i18n.t("sumShort");
  if (!Number.isFinite(amount)) return `0 ${currency}`;
  // tiyin/o'nlik qismi UI da ko'rsatilmaydi
  const integer = Math.trunc(amount);
  const abs = Math.abs(integer);
  const grouped = abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const signed = integer < 0 ? `-${grouped}` : grouped;
  return `${signed} ${currency}`;
}

/** Yig'ilgan / maqsad foizini 0–100 orasida qaytaradi */
export function getProgress(collected: number, goal: number): number {
  if (goal <= 0) return 0;
  return Math.min(Math.round((collected / goal) * 100), 100);
}

/**
 * Summani bo'shliq bilan ajratilgan to'liq raqamda (10 000, 1 500 000).
 * Donate preset tugmalari va boshqa joylarda.
 */
export function formatAmountShort(amount: number): string {
  return amount.toLocaleString(getNumberLocale());
}

/**
 * Summani qisqa birlikda, so'msiz (DonateModal presetlarida "1.5 mln" kabi).
 * formatMoney dan farqi: oxirida valyuta qo'shilmasin.
 */
export function formatAmountCompact(amount: number): string {
  if (!Number.isFinite(amount) || amount < 1e3) return amount.toLocaleString(getNumberLocale());
  return formatShortUnit(amount);
}

/** Inputdan faqat raqamlar qoladi — yetakchi nollar olib tashlanadi (API/state uchun). */
export function parseAmountDigits(input: string): string {
  let d = input.replace(/\D/g, "").slice(0, 18);
  if (/^0+$/.test(d)) return "";
  d = d.replace(/^0+/, "");
  return d;
}

/** Faqat-raqam input qiymatini ko'rinish uchun guruhlaydi: "12500" → "12 500". */
export function formatAmountInputDisplay(digitsOnly: string): string {
  if (!digitsOnly) return "";
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Maskalangan karta raqamini o'qishga qulay ko'rinishga keltiradi:
 * "860049******1234" → "8600 49•• •••• 1234".
 *
 * Provayder maskani turlicha yuborishi mumkin (bo'shliq bilan, `*` yoki `•`
 * bilan) — shuning uchun format qat'iy tekshirilmaydi, faqat 4 talab
 * guruhlanadi va yulduzchalar nuqtaga almashtiriladi.
 */
export function formatMaskedPan(pan: string): string {
  const compact = (pan ?? "").replace(/\s+/g, "");
  if (!compact) return "";
  return (compact.match(/.{1,4}/g) ?? [compact]).join(" ").replace(/\*/g, "•");
}
